import { useState, useCallback, useMemo } from 'react'
import { useCalendarStore } from '@/routes/calendar/CalendarPage.store'
import { Button, Popup } from 'devextreme-react'
import { ColorBox } from 'devextreme-react/color-box'
import { TextBox } from 'devextreme-react/text-box'
import DxCalendar from 'devextreme-react/calendar'
import {
  Column,
  FilterRow,
  RowDragging,
  Selection,
  StateStoring,
  TreeList,
  TreeListTypes,
} from 'devextreme-react/tree-list'
import {
  type CalendarColorPickerTarget,
  type CalendarGroupInputState,
  type CalendarPageDrawerProps,
  type CalendarTreeReorderEvent,
  type FlatCalendarNode,
} from '@/routes/calendar/CalendarPage.types'
import { hslToHex } from '@/routes/calendar/CalendarPage.utils'

const CalendarPageDrawerContent = ({
  miniCalendarRef,
  currentDate,
  displayedFlatCalendarsColored,
  selectedUserIds,
  onMiniCalendarValueChange,
  onSelectedUserIdsChange,
  onTreeReorder,
  onGroupCreate,
  onGroupRename,
  onGroupDelete,
}: CalendarPageDrawerProps) => {
  const { colorOverrides, customMode, saveColorOverride, toggleCustomMode } = useCalendarStore()
  const [groupInputState, setGroupInputState] = useState<CalendarGroupInputState | null>(null)
  const [colorPickerTarget, setColorPickerTarget] = useState<CalendarColorPickerTarget | null>(null)
  const selectedRowKeys = useMemo(
    () =>
      displayedFlatCalendarsColored
        .filter((node) => node.email !== null && selectedUserIds.includes(node.email))
        .map((node) => node.id),
    [displayedFlatCalendarsColored, selectedUserIds],
  )

  const handleGroupConfirm = useCallback(() => {
    if (!groupInputState?.value.trim()) return
    const name = groupInputState.value.trim()
    if (groupInputState.mode === 'create') {
      onGroupCreate(name, groupInputState.parentGroupId)
    } else if (groupInputState.mode === 'rename' && groupInputState.groupId) {
      onGroupRename(groupInputState.groupId, name)
    }
    setGroupInputState(null)
  }, [groupInputState, onGroupCreate, onGroupRename])

  return (
    <div className="calendar-drawer-panel">
      <DxCalendar
        ref={miniCalendarRef}
        defaultValue={currentDate}
        zoomLevel="month"
        maxZoomLevel="month"
        showWeekNumbers={true}
        weekNumberRule="firstFourDays"
        onValueChanged={(e) => {
          if (e.value instanceof Date) {
            onMiniCalendarValueChange(e.value)
          }
        }}
      />
      <div className="calendar-treelist-header">
        <span>Raggruppamento: </span>
        <Button
          text={customMode ? 'Custom' : 'Reparto'}
          icon="group"
          stylingMode="text"
          type={customMode ? 'success' : 'default'}
          hint={customMode ? 'Torna alla vista per reparto' : 'Attiva gruppi personalizzati'}
          onClick={toggleCustomMode}
        />
      </div>
      <TreeList
        id="employees-structure"
        dataSource={displayedFlatCalendarsColored}
        keyExpr="id"
        parentIdExpr="parentId"
        selectedRowKeys={selectedRowKeys}
        className="calendar-treelist"
        showRowLines={true}
        showBorders={true}
        columnAutoWidth={true}
        onSelectionChanged={(e: TreeListTypes.SelectionChangedEvent) => {
          const selectedData = e.component.getSelectedRowsData('leavesOnly')
          const emailArray = selectedData.map((item) => item.email).filter(Boolean) as string[]
          onSelectedUserIdsChange(emailArray)
        }}
        onRowClick={(e: TreeListTypes.RowClickEvent) => {
          if ((e.event?.target as HTMLElement)?.closest('.dx-select-checkbox')) return
          if ((e.event?.target as HTMLElement)?.closest('.dx-treelist-collapsed, .dx-treelist-expanded')) return
          if ((e.event?.target as HTMLElement)?.closest('.dx-command-drag')) return
          if ((e.event?.target as HTMLElement)?.closest('.calendar-color-swatch')) return
          const key = e.key
          if (e.component.isRowSelected(key)) {
            e.component.deselectRows([key])
          } else {
            e.component.selectRows([key], true)
          }
        }}
        onContextMenuPreparing={(e: TreeListTypes.ContextMenuPreparingEvent) => {
          if (!customMode) return
          if (!e.row) {
            e.items = [
              {
                icon: 'plus',
                text: 'Nuovo gruppo',
                onItemClick: () => setGroupInputState({ mode: 'create', value: '' }),
              },
            ]
            return
          }

          const node = e.row.data as FlatCalendarNode
          if (node.nodeType === 'leaf') {
            e.items = undefined
            return
          }

          const items: any[] = []

          if (node.nodeType === 'unassigned') {
            items.push({
              icon: 'plus',
              text: 'Nuovo gruppo',
              onItemClick: () => setGroupInputState({ mode: 'create', value: '' }),
            })
            e.items = items
            return
          }

          if (node.nodeType === 'group') {
            items.push(
              {
                icon: 'plus',
                text: 'Nuovo sottogruppo',
                onItemClick: () => setGroupInputState({ mode: 'create', parentGroupId: node.id, value: '' }),
              },
              {
                icon: 'edit',
                text: 'Rinomina',
                onItemClick: () => setGroupInputState({ mode: 'rename', groupId: node.id, value: node.fullname }),
              },
              { icon: 'minus', text: 'Elimina', onItemClick: () => onGroupDelete(node.id) },
            )
          }

          e.items = items
        }}
      >
        <RowDragging
          allowReordering={true}
          allowDropInsideItem={customMode}
          onReorder={(e) => onTreeReorder(e as CalendarTreeReorderEvent)}
        />
        <Selection recursive={true} mode="multiple" />
        <StateStoring enabled={true} type="localStorage" storageKey="users-calendar-tree" />
        <FilterRow visible={true} />
        <Column
          dataField="fullname"
          caption="Calendari"
          cellRender={(cellData) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {cellData.data.color && cellData.data.email && (
                <button
                  type="button"
                  className="calendar-color-swatch"
                  title="Cambia colore"
                  onClick={(e) => {
                    e.stopPropagation()
                    setColorPickerTarget({
                      email: cellData.data.email,
                      fullname: cellData.data.fullname,
                      color: hslToHex(cellData.data.color),
                    })
                  }}
                  style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: cellData.data.color,
                    flexShrink: 0,
                    cursor: 'pointer',
                    border: 'none',
                    padding: 0,
                    outline: 'none',
                  }}
                />
              )}
              {cellData.data.color && !cellData.data.email && (
                <span
                  style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: cellData.data.color,
                    flexShrink: 0,
                  }}
                />
              )}
              {cellData.value}
            </div>
          )}
        />
      </TreeList>

      {/* ── Group input popup ────────────────────────────────────────────── */}
      <Popup
        visible={groupInputState !== null}
        title={
          groupInputState?.mode === 'rename'
            ? 'Rinomina gruppo'
            : groupInputState?.parentGroupId
              ? 'Nuovo gruppo figlio'
              : 'Nuovo gruppo'
        }
        width={300}
        height={170}
        showCloseButton={true}
        onHiding={() => setGroupInputState(null)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
          <TextBox
            value={groupInputState?.value ?? ''}
            onValueChanged={(e) => setGroupInputState((prev) => (prev ? { ...prev, value: e.value } : null))}
            onEnterKey={handleGroupConfirm}
          />
          <Button text="Conferma" type="default" stylingMode="contained" onClick={handleGroupConfirm} />
        </div>
      </Popup>

      {/* ── Color picker popup ────────────────────────────────────────────── */}
      <Popup
        shading={true}
        shadingColor="transparent"
        visible={colorPickerTarget !== null}
        title="Colore calendario"
        width={260}
        height="auto"
        showCloseButton={true}
        onHiding={() => setColorPickerTarget(null)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
          {colorPickerTarget && (
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{colorPickerTarget.fullname}</div>
          )}
          <ColorBox
            value={colorPickerTarget?.color ?? ''}
            editAlphaChannel={false}
            applyValueMode="instantly"
            onValueChanged={(ev) => {
              if (!colorPickerTarget) return
              if (!ev.value) return
              if (ev.value.toLowerCase() === colorPickerTarget.color.toLowerCase()) return
              saveColorOverride(colorPickerTarget.email, ev.value)
              setColorPickerTarget((prev) => (prev ? { ...prev, color: ev.value } : null))
            }}
          />
          {colorPickerTarget && colorOverrides[colorPickerTarget.email] && (
            <Button
              text="Ripristina"
              type="default"
              stylingMode="contained"
              onClick={() => {
                saveColorOverride(colorPickerTarget!.email, null)
                setColorPickerTarget(null)
              }}
            />
          )}
        </div>
      </Popup>
    </div>
  )
}

export default CalendarPageDrawerContent
